import { useCallback, useRef, useState } from "react";
import HoverTooltip from "../HoverTooltip.jsx";
import { NOTE_CLASS } from "../../lib/ui.js";
import { fetchQuestionRows, standardIdFor, triageErrorText, writeStandard } from "../../lib/findingsTriage.js";
import { questionRowOf } from "../../lib/investigateAgent.js";
import { updateSuggestion } from "../../lib/investigateStore.js";
import { ACTION_CHIP, FILE_LABEL } from "./triageParts.jsx";
import { usd } from "./InvestigateParts.jsx";

// Standard edits Investigate drafted from what slipped past the standards:
// each says why, what it should catch and leave alone, and can be tried here
// on exactly those findings before anyone adds it. Trying asks the
// backend's own question in this browser (standardPreview) and keeps only
// the tally; adding writes the standard, live for new findings from then on.

function useTry({ scopeKey, placedById, window }) {
  const [trying, setTrying] = useState(null);
  const controller = useRef(null);
  const run = useCallback(async (s) => {
    controller.current?.abort();
    const ctl = new AbortController();
    controller.current = ctl;
    setTrying({ id: s.id, done: 0, total: s.catches.length + s.spares.length, error: "" });
    try {
      const ids = [...s.catches, ...s.spares];
      // The backend's row where it checked the finding; a row built from the
      // verdict where Investigate checked or corrected it here.
      const backend = await fetchQuestionRows({ ids: ids.filter((id) => placedById.get(id)?.source === "check"), reviewWindow: window, signal: ctl.signal });
      const rows = ids.map((id) => {
        const p = placedById.get(id);
        if (!p?.verdict) return null;
        return p.source === "check" ? backend[id] || backend[p.finding.id] || null : questionRowOf(p.finding, p.verdict);
      }).filter(Boolean);
      const { openPreviewModel, previewStandard } = await import("../../lib/standardPreview.js");
      const model = await openPreviewModel();
      const met = new Set();
      const result = await previewStandard({
        standard: { standardId: s.standardId || s.name, text: s.text }, rows, complete: model.complete, signal: ctl.signal,
        onAnswer: ({ row, outcome }) => { if (outcome === "met") met.add(String(row.finding_id).toLowerCase()); },
        onProgress: ({ done, total }) => setTrying((cur) => (cur?.id === s.id ? { ...cur, done, total } : cur)),
      });
      if (ctl.signal.aborted) return;
      const catches = new Set(s.catches);
      const asked = rows.map((r) => String(r.finding_id).toLowerCase());
      updateSuggestion(scopeKey, s.id, {
        tried: {
          caught: asked.filter((id) => catches.has(id) && met.has(id)).length,
          of: asked.filter((id) => catches.has(id)).length,
          spared: asked.filter((id) => !catches.has(id) && !met.has(id)).length,
          ofSpared: asked.filter((id) => !catches.has(id)).length,
          costUsd: result.costUsd,
          at: Date.now(),
        },
      });
      setTrying(null);
    } catch (error) {
      if (!ctl.signal.aborted) setTrying({ id: s.id, done: 0, total: 0, error: String(error?.message || error) });
    }
  }, [scopeKey, placedById, window]);
  const stop = useCallback(() => { controller.current?.abort(); setTrying(null); }, []);
  return { trying, run, stop };
}

function SuggestionCard({ s, std, trying, onTry, onStopTry, onAdd, onDismiss, adding }) {
  const [open, setOpen] = useState(false);
  const edit = s.kind === "edit" ? std.lib.items.find((x) => x.standardId === s.standardId) : null;
  const t = s.tried;
  return (
    <div data-investigate-suggestion={s.kind} className="grid gap-[6px] border-t border-[color:var(--line-soft)] px-[6px] py-[8px] first:border-t-0">
      <div className="flex min-w-0 flex-wrap items-baseline gap-x-[10px] gap-y-[2px]">
        <span className="text-[12px] leading-[1.2] font-medium text-[color:var(--ink)]">{edit ? `Edit ${edit.name}` : `New standard: ${s.name}`}</span>
        <span className="text-[11px] leading-[1.2] text-[color:var(--muted)]">should catch {s.catches.length} finding{s.catches.length === 1 ? "" : "s"}{s.spares.length ? `, leave ${s.spares.length} alone` : ""}</span>
      </div>
      {s.why ? <span className="text-[11px] leading-[1.45] text-[color:var(--ink)]">{s.why}</span> : null}
      {t ? (
        <span data-investigate-tried="" className="text-[11px] leading-[1.4] text-[color:var(--ink)]">
          Tried here: met {t.caught} of the {t.of} it should catch{t.ofSpared ? `, and left ${t.spared} of the ${t.ofSpared} it should leave alone` : ""} {"\u00b7"} {usd(t.costUsd)}
        </span>
      ) : null}
      {trying?.id === s.id ? (
        <span role="status" className={NOTE_CLASS}>{trying.error ? `Couldn't try it: ${trying.error}` : `Trying it on ${trying.total} findings\u2026 ${trying.done} asked`}</span>
      ) : null}
      {open ? <pre data-investigate-suggestion-text="" className="m-0 max-h-[220px] overflow-auto rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] p-[8px] text-[11px] leading-[1.5] whitespace-pre-wrap text-[color:var(--ink)]">{s.text}</pre> : null}
      <div className="flex flex-wrap items-center gap-[6px]">
        <button type="button" onClick={() => setOpen((v) => !v)} className={ACTION_CHIP}>{open ? "Hide wording" : "Read wording"}</button>
        {trying?.id === s.id && !trying.error
          ? <button type="button" onClick={onStopTry} className={ACTION_CHIP}>Stop trying</button>
          : (
            <HoverTooltip label="Asks the wording, in this browser, of the findings it should catch and the ones it should leave alone. Nothing is saved but the tally." placement="top" wide>
              <button type="button" data-investigate-try="" onClick={() => onTry(s)} disabled={!std.sensitive} className={`${ACTION_CHIP} disabled:opacity-50`}>{t ? "Try again" : "Try it"}</button>
            </HoverTooltip>
          )}
        {std.canWrite ? (
          <button type="button" data-investigate-add="" disabled={adding} onClick={() => onAdd(s, edit)} className={`${ACTION_CHIP} border-[color:var(--line-strong)] text-[color:var(--ink)] disabled:opacity-50`}>
            {adding ? "Saving\u2026" : edit ? "Save the edit" : "Add the standard"}
          </button>
        ) : <span className={NOTE_CLASS}>You need permission to change tenant settings to add standards.</span>}
        <button type="button" onClick={() => onDismiss(s)} className={ACTION_CHIP}>Dismiss</button>
      </div>
    </div>
  );
}

export default function InvestigateSuggestions({ scopeKey, suggestions, std, placedById, window, onAdded }) {
  const { trying, run, stop } = useTry({ scopeKey, placedById, window });
  const [adding, setAdding] = useState(null);
  const [error, setError] = useState("");
  const shown = suggestions.filter((s) => !s.dismissed && !s.added);
  const add = useCallback(async (s, edit) => {
    setAdding(s.id);
    setError("");
    try {
      const item = edit
        ? await writeStandard({ standardId: edit.standardId, expectedVersion: edit.version, name: edit.name, text: s.text })
        : await writeStandard({ standardId: standardIdFor(s.name), expectedVersion: 0, name: s.name, text: s.text, live: true });
      updateSuggestion(scopeKey, s.id, { added: item.id || item.standardId || "yes" });
      std.reload();
      onAdded?.(item);
    } catch (e) {
      setError(triageErrorText(e));
    } finally {
      setAdding(null);
    }
  }, [scopeKey, std, onAdded]);
  if (!shown.length) return null;
  return (
    <section data-investigate-suggestions="" className="grid gap-[2px] rounded-[2px] border border-[color:var(--line)] py-[4px]">
      <span className={`${FILE_LABEL} px-[6px] pt-[4px]`}>Standards that would have caught what slipped past</span>
      {shown.map((s) => (
        <SuggestionCard key={s.id} s={s} std={std} trying={trying} onTry={run} onStopTry={stop} onAdd={add} adding={adding === s.id} onDismiss={(x) => updateSuggestion(scopeKey, x.id, { dismissed: true })} />
      ))}
      {error ? <span role="alert" className={`${NOTE_CLASS} px-[6px]`}>{error}</span> : null}
    </section>
  );
}
